import React, { useContext, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  getFrameworks,
  clearSuppressionState,
} from "../../../../store/suppression/suppressionSlice";
import { formatScheduleName, getSuppressionAccountLabels } from "../utils/scheduleNameFormatter";
import {
  CloudProviderContext,
  shouldShowAzurePlaceholderView,
} from "../../../../CloudProviderContext";
import {
  getComplianceIcon,
  getComplianceFrameworkBadgeText,
} from "../../../../assets/images/compliance/IconCompliance.jsx";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../../components/AccountDetailPolicy/accountDetailTypography";

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const BACK_BTN_SX = {
  color: "#475569",
  backgroundColor: "#fff",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 600,
  px: 2,
  py: 1,
  height: 40,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#f8fafc",
    color: "#111827",
    borderColor: "#94a3b8",
    boxShadow: "none",
  },
};

const SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 400 },
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#e2e8f0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const EDIT_BTN_SX = {
  textTransform: "none",
  fontSize: "0.8125rem",
  fontWeight: 600,
  padding: "6px 14px",
  borderRadius: "8px",
  borderColor: BRAND_ORANGE,
  color: BRAND_ORANGE,
  backgroundColor: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#fff4eb",
    borderColor: BRAND_ORANGE_HOVER,
    color: BRAND_ORANGE_HOVER,
    boxShadow: "none",
  },
};

const Frameworks = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { scheduleId } = useParams();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accountLabels = getSuppressionAccountLabels(
    shouldShowAzurePlaceholderView(selectedProvider)
  );

  const { frameworks, isFetchingFrameworks, fetchFrameworksError } = useSelector(
    (state) => state.suppression
  );

  // Get selected schedule from sessionStorage
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Sorting state
  const [sortField, setSortField] = useState('control_suppression_count'); // 'name', 'control_suppression_count', 'check_suppression_count'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  
  // Pagination state
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100; // API fetches 100 records at a time
  const DEFAULT_PAGE_SIZE = 25;
  
  // Get page size from localStorage or use default
  const getStoredPageSize = () => {
    const stored = localStorage.getItem("suppression_frameworks_page_size");
    return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
  };
  
  const [pageSize, setPageSize] = useState(getStoredPageSize());
  
  // Store all frameworks data and track fetched API pages
  const [allFrameworks, setAllFrameworks] = useState(null);
  const [fetchedApiPages, setFetchedApiPages] = useState(new Set([1]));

  useEffect(() => {
    // Retrieve schedule data from sessionStorage
    const scheduleData = sessionStorage.getItem("selectedSchedule");
    if (scheduleData) {
      try {
        setSelectedSchedule(JSON.parse(scheduleData));
      } catch (error) {
        console.error("Error parsing selectedSchedule:", error);
      }
    }
  }, []);

  // Fetch frameworks on mount
  useEffect(() => {
    if (scheduleId) {
      dispatch(getFrameworks({ 
        schedule_id: scheduleId,
        page: 1,
        page_size: API_PAGE_SIZE,
      }));
    }
  }, [dispatch, scheduleId]);
  
  // Check if we need to fetch more data when page changes
  useEffect(() => {
    if (!allFrameworks?.items || !scheduleId) return;
    
    // Calculate which API page we need based on current UI page and page size
    const recordsNeeded = page * pageSize;
    const apiPageNeeded = Math.ceil(recordsNeeded / API_PAGE_SIZE);
    
    // If we haven't fetched this API page yet, fetch it
    if (apiPageNeeded > 0 && !fetchedApiPages.has(apiPageNeeded)) {
      dispatch(getFrameworks({ 
        schedule_id: scheduleId,
        page: apiPageNeeded,
        page_size: API_PAGE_SIZE,
      }));
      setFetchedApiPages((prev) => new Set([...prev, apiPageNeeded]));
    }
  }, [page, pageSize, allFrameworks, fetchedApiPages, dispatch, scheduleId]);
  
  // Update allFrameworks when frameworks data is received (merge with existing data)
  useEffect(() => {
    if (frameworks && !isFetchingFrameworks && frameworks.items) {
      setAllFrameworks((prev) => {
        if (!prev) {
          return frameworks;
        }
        // Merge items, avoiding duplicates
        const existingIds = new Set(prev.items.map((f) => f.id));
        const newItems = frameworks.items.filter(
          (f) => !existingIds.has(f.id)
        );
        return {
          ...frameworks,
          items: [...prev.items, ...newItems],
          total: Math.max(prev.total || 0, frameworks.total || 0),
        };
      });
    }
  }, [frameworks, isFetchingFrameworks]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSuppressionState());
    };
  }, [dispatch]);

  // Handle back navigation
  const handleBack = () => {
    history.push("/configuration/suppression");
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (path) => {
    history.push(path);
  };

  // Handle view controls button click
  const handleViewControls = (framework) => {
    // Store framework and schedule data in sessionStorage
    const controlsData = {
      framework: framework,
      schedule: selectedSchedule,
      scheduleId: scheduleId,
    };
    sessionStorage.setItem("selectedFramework", JSON.stringify(controlsData));
    history.push(`/configuration/suppression/frameworks/${scheduleId}/controls/${framework.id}`);
  };

  // Filter and paginate frameworks client-side
  const filteredFrameworks = useMemo(() => {
    if (!allFrameworks?.items) return { items: [], total: 0, page: 1, page_size: pageSize };
    
    let filtered = [...allFrameworks.items];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((framework) => {
        return (
          framework.name?.toLowerCase().includes(searchLower) ||
          framework.compliance_key?.toLowerCase().includes(searchLower) ||
          framework.description?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Sort data - always use control count first (desc), then check count (desc) as secondary
    filtered.sort((a, b) => {
      if (sortField === 'name') {
        // For name sorting, just sort by name
        const aValue = (a.name || '').toLowerCase();
        const bValue = (b.name || '').toLowerCase();
        let comparison = 0;
        if (aValue < bValue) {
          comparison = -1;
        } else if (aValue > bValue) {
          comparison = 1;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        // For count sorting, ALWAYS sort by control count first (desc), then check count (desc)
        const aControl = a.control_suppression_count ?? 0;
        const bControl = b.control_suppression_count ?? 0;
        const aCheck = a.check_suppression_count ?? 0;
        const bCheck = b.check_suppression_count ?? 0;
        
        // Primary sort: control count (descending)
        if (bControl !== aControl) {
          return bControl - aControl;
        }
        
        // Secondary sort: check count (descending)
        return bCheck - aCheck;
      }
    });

    // Calculate pagination
    const total = filtered.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      total,
      page,
      page_size: pageSize,
    };
  }, [allFrameworks, searchTerm, page, pageSize, sortField, sortDirection]);
  
  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with descending direction for counts, ascending for name
      setSortField(field);
      setSortDirection(field === 'name' ? 'asc' : 'desc');
    }
    setPage(1); // Reset to first page when sorting changes
  };
  
  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    localStorage.setItem("suppression_frameworks_page_size", newPageSize.toString());
    setPage(1); // Reset to first page when page size changes
  };
  
  // Get pagination info
  const totalPages = filteredFrameworks?.total
    ? Math.ceil(filteredFrameworks.total / pageSize)
    : 0;
  const currentPageStart = filteredFrameworks?.items?.length
    ? (page - 1) * pageSize + 1
    : 0;
  const currentPageEnd = filteredFrameworks?.items?.length
    ? Math.min(page * pageSize, filteredFrameworks.total || 0)
    : 0;

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string")
        return error.message;
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      try {
        const stringified = JSON.stringify(error);
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem 2rem" },
        mb: "3rem",
        backgroundColor: "#fff",
        minHeight: "100vh",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2.5 }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick("/configuration/suppression")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Schedules
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#1e293b",
              fontSize: "0.875rem",
              fontWeight: 500,
              px: 0.5,
            }}
          >
            Frameworks
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header with Back Button on Right */}
      <Box sx={{ 
        mb: 2.5, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        borderBottom: "1px solid #e5e7eb",
        pb: 1.5
      }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#374151",
              fontSize: "1rem",
              letterSpacing: "-0.01em",
              mb: 0.25,
            }}
          >
            Compliance Frameworks
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              fontSize: "0.8125rem",
              fontWeight: 400,
            }}
          >
            {selectedSchedule
              ? `Schedule: ${formatScheduleName(selectedSchedule)} | ${accountLabels.noun}: ${selectedSchedule.account_number || selectedSchedule.account_id || "N/A"}`
              : "Viewing frameworks for selected schedule"}
          </Typography>
        </Box>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={BACK_BTN_SX}
        >
          Back
        </Button>
      </Box>

      {/* Loading State */}
      {isFetchingFrameworks && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={40} sx={{ color: BRAND_ORANGE }} />
          <Typography
            variant="body2"
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading frameworks...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {fetchFrameworksError && !isFetchingFrameworks && (
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #f87171",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchFrameworksError)}
          </Typography>
        </Box>
      )}

      {/* Frameworks Table */}
      {frameworks && !isFetchingFrameworks && !fetchFrameworksError && (
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                }}
              >
                Frameworks
              </Typography>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
                sx={SEARCH_FIELD_SX}
              />
            </Box>

            <TableContainer
              sx={{
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fff" }}>
                    <TableCell
                      onClick={() => handleSort('name')}
                      sx={{
                        fontWeight: 600,
                        color: "#64748b",
                        fontSize: "0.6875rem",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        py: 1.25,
                        cursor: "pointer",
                        userSelect: "none",
                        borderBottom: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Name
                        {sortField === 'name' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('control_suppression_count')}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#64748b",
                        fontSize: "0.6875rem",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        py: 1.25,
                        cursor: "pointer",
                        userSelect: "none",
                        borderBottom: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        Control Suppression Count
                        {sortField === 'control_suppression_count' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('check_suppression_count')}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#64748b",
                        fontSize: "0.6875rem",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        py: 1.25,
                        cursor: "pointer",
                        userSelect: "none",
                        borderBottom: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        Check Suppression Count
                        {sortField === 'check_suppression_count' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#64748b",
                        fontSize: "0.6875rem",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        py: 1.25,
                        width: "140px",
                        pr: 4,
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFrameworks?.items && filteredFrameworks.items.length > 0 ? (
                    filteredFrameworks.items.map((framework, index) => {
                      const isExpanded = expandedRow === (framework.id || index);
                      return (
                        <React.Fragment key={framework.id || index}>
                          <TableRow
                            sx={{
                              "&:hover": {
                                backgroundColor: "#f8fafc",
                                "& td": {
                                  backgroundColor: "#f8fafc",
                                },
                              },
                            }}
                          >
                            <TableCell sx={{ py: 0.25 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setExpandedRow(isExpanded ? null : (framework.id || index));
                                  }}
                                  sx={{
                                    padding: "4px",
                                    color: "#6b7280",
                                    "&:hover": {
                                      backgroundColor: "#f3f4f6",
                                    },
                                  }}
                                >
                                  {isExpanded ? (
                                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                                {(() => {
                                  const fwName = framework.name || "";
                                  const iconSrc = getComplianceIcon(fwName);
                                  const badgeText = getComplianceFrameworkBadgeText(fwName);
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 24,
                                        height: 24,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {iconSrc ? (
                                        <img
                                          src={iconSrc}
                                          alt={(fwName || "Framework") + " icon"}
                                          style={{
                                            height: 24,
                                            width: "auto",
                                            maxWidth: 32,
                                            objectFit: "contain",
                                          }}
                                        />
                                      ) : badgeText ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 800,
                                            color: "#1976d2",
                                            fontSize: "0.65rem",
                                            letterSpacing: 0.3,
                                            lineHeight: 1.1,
                                            textAlign: "center",
                                          }}
                                        >
                                          {badgeText}
                                        </Typography>
                                      ) : (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 700,
                                            color: "#1976d2",
                                            fontSize: "0.7rem",
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {(fwName || "N").charAt(0)}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#374151",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {framework.name || "N/A"}
                                  {((framework.control_suppression_count ?? 0) > 0 || (framework.check_suppression_count ?? 0) > 0) && (
                                    <Tooltip title="Suppressed" arrow>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: "#f59e0b",
                                          display: "inline-block",
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.25 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  lineHeight: 1.3,
                                }}
                              >
                                {framework.control_suppression_count ?? 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.25 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  lineHeight: 1.3,
                                }}
                              >
                                {framework.check_suppression_count ?? 0}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.25, pr: 4 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleViewControls(framework)}
                                sx={EDIT_BTN_SX}
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell
                                colSpan={4}
                                sx={{
                                  py: 2,
                                  px: 3,
                                  backgroundColor: "#fafafa",
                                  borderTop: "1px solid #e5e7eb",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#6b7280",
                                    fontSize: "0.8125rem",
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {framework.description || "No description available"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 4, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: "0.875rem",
                          }}
                        >
                          No frameworks found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                minHeight: 40,
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#6b7280", fontSize: "0.75rem" }}
                >
                  Rows per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      fontSize: "0.75rem",
                      height: "28px",
                      "& .MuiSelect-select": {
                        py: 0.5,
                        px: 1,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Page {page} of {totalPages || 1}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <FirstPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <LastPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Frameworks;

